import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { MdCloudUpload, MdDownload } from 'react-icons/md';
import { useUser } from '../context/UserContext';
import {
  cargarLeadsMasivo,
  getEjecutivosCargaMasivaLeads,
  getLeadOrigenSelect,
} from '../api/apiConfig';

const APPLY_CHUNK_SIZE = 500;
const HOJA_LEADS = 'LEADS';
const HOJA_EJECUTIVOS = 'EJECUTIVOS';
const HOJA_ORIGENES = 'ORIGENES';

const MOTIVOS = {
  fila_no_objeto: 'Fila con formato inesperado',
  rut_invalido: 'RUT vacío o con formato inválido',
  digito_verificador_invalido: 'Dígito verificador no corresponde al RUT',
  ejecutivo_vacio: 'Falta el ejecutivo',
  ejecutivo_no_encontrado: 'El ejecutivo no está en la hoja EJECUTIVOS',
  ejecutivo_ambiguo: 'Hay más de un ejecutivo con ese nombre; usa el correo',
  ejecutivo_sin_equipo: 'El ejecutivo no tiene equipo (GRUPO) en ejecutivos_estado',
  origen_vacio: 'Falta el origen',
  origen_no_valido: 'El origen no está en la hoja ORIGENES',
  fecha_invalida: 'Fecha de asignación con formato inválido',
  rut_duplicado_en_archivo: 'RUT repetido en el archivo; se conserva la primera fila',
  lead_ya_existe: 'Ya existe como lead',
  cliente_con_linea_vigente: 'Ya es cliente con línea y ejecutivo asignado',
};

const describirMotivo = (motivo) => MOTIVOS[motivo] || motivo || '-';

const normalizeKey = (v) => String(v ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const normalizeHeader = (v) => String(v ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/[_\-.]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const findHeader = (headers, aliases) => {
  const set = new Set(aliases.map(normalizeHeader));
  return headers.find((h) => set.has(normalizeHeader(h))) || null;
};

/** Deja el RUT como '12345678-9', el formato guardado en MGC.leads.RUT_LEAD. */
const normalizeRut = (value) => {
  const raw = String(value ?? '').toUpperCase().replace(/[^0-9K]/g, '');
  if (raw.length < 8 || raw.length > 9) return null;
  const cuerpo = raw.slice(0, -1);
  const dv = raw.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return null;
  const cuerpoNum = Number(cuerpo);
  if (!cuerpoNum) return null;
  return `${cuerpoNum}-${dv}`;
};

const rutDvOk = (rut) => {
  const [cuerpo, dv] = rut.split('-');
  let suma = 0;
  let factor = 2;
  for (let i = cuerpo.length - 1; i >= 0; i -= 1) {
    suma += Number(cuerpo[i]) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const resto = 11 - (suma % 11);
  const esperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
  return dv === esperado;
};

/** Acepta texto (YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY) y el serial numérico de Excel. */
const normalizeFecha = (value) => {
  if (value == null || value === '') return { ok: true, valor: null };
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { ok: true, valor: value.toISOString().slice(0, 10) };
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF?.parse_date_code?.(value);
    if (!parsed) return { ok: false };
    const mm = String(parsed.m).padStart(2, '0');
    const dd = String(parsed.d).padStart(2, '0');
    return { ok: true, valor: `${parsed.y}-${mm}-${dd}` };
  }
  const txt = String(value).trim();
  if (!txt) return { ok: true, valor: null };
  let m = txt.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) {
    return { ok: true, valor: `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` };
  }
  m = txt.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    return { ok: true, valor: `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` };
  }
  return { ok: false };
};

const parseListaRespuesta = (result, campo) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.[campo])) return result[campo];
  if (typeof result?.body === 'string') {
    try {
      const parsed = JSON.parse(result.body);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.[campo])) return parsed[campo];
    } catch {
      /* body no JSON */
    }
  }
  return [];
};

const CargaMasivaLeads = () => {
  const { user } = useUser();
  const [ejecutivos, setEjecutivos] = useState([]);
  const [origenes, setOrigenes] = useState([]);
  const [catalogosError, setCatalogosError] = useState('');
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [nombreArchivo, setNombreArchivo] = useState('');
  const [rows, setRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [reasignarExistentes, setReasignarExistentes] = useState(false);
  const [reiniciarEstado, setReiniciarEstado] = useState(true);
  const [permitirConLinea, setPermitirConLinea] = useState(true);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelado = false;

    const cargar = async () => {
      setCargandoCatalogos(true);
      setCatalogosError('');
      try {
        const [ejecResp, origenResp] = await Promise.all([
          getEjecutivosCargaMasivaLeads(user),
          getLeadOrigenSelect(),
        ]);
        if (cancelado) return;

        const listaEjec = parseListaRespuesta(ejecResp, 'ejecutivos');
        if (ejecResp?.ok === false) {
          throw new Error(ejecResp?.error || 'No se pudo obtener el listado de ejecutivos');
        }
        setEjecutivos(listaEjec);
        setOrigenes(parseListaRespuesta(origenResp, 'origenes'));
      } catch (error) {
        if (cancelado) return;
        setCatalogosError(error?.message || 'No se pudieron cargar los catálogos');
      } finally {
        if (!cancelado) setCargandoCatalogos(false);
      }
    };

    cargar();
    return () => {
      cancelado = true;
    };
  }, [user]);

  /** Un ejecutivo se puede escribir por nombre, correo o usuario SF. */
  const indiceEjecutivos = useMemo(() => {
    const index = new Map();
    ejecutivos.forEach((e) => {
      const usuarioSf = e.usuario_sf || e.EJEC_SIST_FACTORING;
      if (!usuarioSf) return;
      const entry = {
        usuario_sf: usuarioSf,
        nombre: e.nombre || e.NOMBRE || '',
        mail: e.mail || e.MAIL || '',
        equipo: e.equipo || e.GRUPO || '',
      };
      [entry.nombre, entry.mail, entry.usuario_sf].forEach((valor) => {
        const key = normalizeKey(valor);
        if (!key) return;
        const bucket = index.get(key) || [];
        if (!bucket.some((x) => x.usuario_sf === entry.usuario_sf)) bucket.push(entry);
        index.set(key, bucket);
      });
    });
    return index;
  }, [ejecutivos]);

  const indiceOrigenes = useMemo(() => {
    const index = new Map();
    origenes.forEach((o) => {
      const valor = o.ORIGEN || o.origen;
      const key = normalizeKey(valor);
      if (key) index.set(key, valor);
    });
    return index;
  }, [origenes]);

  const canSubmit = Boolean(user) && rows.length > 0 && !loading;

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    const hojaLeads = XLSX.utils.aoa_to_sheet([
      ['RUT', 'EJECUTIVO', 'ORIGEN', 'FECHA_ASIG'],
      ['76099755-2', ejecutivos[0]?.mail || 'ejecutivo@interfactor.cl', origenes[0]?.ORIGEN || '', ''],
    ]);
    hojaLeads['!cols'] = [{ wch: 14 }, { wch: 34 }, { wch: 32 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, hojaLeads, HOJA_LEADS);

    const hojaEjecutivos = XLSX.utils.aoa_to_sheet([
      ['NOMBRE', 'CORREO', 'USUARIO_SF', 'EQUIPO'],
      ...ejecutivos.map((e) => [
        e.nombre || '',
        e.mail || '',
        e.usuario_sf || '',
        e.equipo || '',
      ]),
    ]);
    hojaEjecutivos['!cols'] = [{ wch: 34 }, { wch: 34 }, { wch: 20 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, hojaEjecutivos, HOJA_EJECUTIVOS);

    const hojaOrigenes = XLSX.utils.aoa_to_sheet([
      ['CATEGORIA', 'ORIGEN'],
      ...origenes.map((o) => [o.CATEGORIA || '', o.ORIGEN || '']),
    ]);
    hojaOrigenes['!cols'] = [{ wch: 28 }, { wch: 36 }];
    XLSX.utils.book_append_sheet(wb, hojaOrigenes, HOJA_ORIGENES);

    XLSX.writeFile(wb, `plantilla_carga_masiva_leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setResultado(null);
    setNombreArchivo(file.name);

    try {
      const bytes = await file.arrayBuffer();
      const wb = XLSX.read(bytes, { type: 'array', cellDates: true });
      const hoja = wb.SheetNames.find((n) => normalizeHeader(n) === HOJA_LEADS) || wb.SheetNames[0];
      const json = XLSX.utils.sheet_to_json(wb.Sheets[hoja], { defval: '' });

      if (!Array.isArray(json) || json.length === 0) {
        setRows([]);
        setInvalidRows([]);
        setMessage(`La hoja "${hoja}" no contiene datos.`);
        setMessageType('error');
        return;
      }

      const headers = Object.keys(json[0] || {});
      const rutHeader = findHeader(headers, ['RUT', 'RUT LEAD', 'RUT_LEAD']);
      const ejecHeader = findHeader(headers, ['EJECUTIVO', 'EJEC ASIG', 'EJEC_ASIG', 'CORREO', 'MAIL']);
      const origenHeader = findHeader(headers, ['ORIGEN']);
      const fechaHeader = findHeader(headers, ['FECHA ASIG', 'FECHA_ASIG', 'FECHA', 'FECHA ASIGNACION']);

      if (!rutHeader || !ejecHeader || !origenHeader) {
        setRows([]);
        setInvalidRows([]);
        setMessage('Faltan columnas obligatorias en la hoja LEADS: se requieren RUT, EJECUTIVO y ORIGEN.');
        setMessageType('error');
        return;
      }

      const validas = [];
      const invalidas = [];
      const vistos = new Map();

      json.forEach((r, idx) => {
        const fila = idx + 2; // +1 por el encabezado, +1 porque Excel parte en 1
        const rut = normalizeRut(r[rutHeader]);
        if (!rut) {
          invalidas.push({ fila, rut: String(r[rutHeader] ?? '').trim(), motivo: 'rut_invalido' });
          return;
        }
        if (!rutDvOk(rut)) {
          invalidas.push({ fila, rut, motivo: 'digito_verificador_invalido' });
          return;
        }

        const ejecRaw = String(r[ejecHeader] ?? '').trim();
        const candidatos = indiceEjecutivos.get(normalizeKey(ejecRaw)) || [];
        if (!ejecRaw) {
          invalidas.push({ fila, rut, motivo: 'ejecutivo_vacio' });
          return;
        }
        if (candidatos.length === 0) {
          invalidas.push({ fila, rut, ejecutivo: ejecRaw, motivo: 'ejecutivo_no_encontrado' });
          return;
        }
        if (candidatos.length > 1) {
          invalidas.push({ fila, rut, ejecutivo: ejecRaw, motivo: 'ejecutivo_ambiguo' });
          return;
        }
        const ejecutivo = candidatos[0];
        if (!ejecutivo.equipo) {
          invalidas.push({ fila, rut, ejecutivo: ejecRaw, motivo: 'ejecutivo_sin_equipo' });
          return;
        }

        const origenRaw = String(r[origenHeader] ?? '').trim();
        if (!origenRaw) {
          invalidas.push({ fila, rut, motivo: 'origen_vacio' });
          return;
        }
        const origen = indiceOrigenes.get(normalizeKey(origenRaw));
        if (!origen) {
          invalidas.push({ fila, rut, origen: origenRaw, motivo: 'origen_no_valido' });
          return;
        }

        const fecha = normalizeFecha(fechaHeader ? r[fechaHeader] : '');
        if (!fecha.ok) {
          invalidas.push({ fila, rut, motivo: 'fecha_invalida' });
          return;
        }

        const previa = vistos.get(rut);
        if (previa) {
          invalidas.push({ fila, rut, motivo: 'rut_duplicado_en_archivo', detalle: `Primera aparición en la fila ${previa}` });
          return;
        }
        vistos.set(rut, fila);

        validas.push({
          fila,
          rut,
          ejecutivo: ejecutivo.usuario_sf,
          ejecutivo_nombre: ejecutivo.nombre,
          equipo: ejecutivo.equipo,
          origen,
          ...(fecha.valor ? { fecha_asig: fecha.valor } : {}),
        });
      });

      setRows(validas);
      setInvalidRows(invalidas);
      setMessage(
        `Archivo leído (hoja "${hoja}"): ${validas.length} filas válidas` +
          (invalidas.length ? `, ${invalidas.length} con problemas.` : '.'),
      );
      setMessageType(validas.length ? 'success' : 'error');
    } catch (error) {
      setRows([]);
      setInvalidRows([]);
      setMessage(`No se pudo leer el archivo: ${error?.message || 'error desconocido'}`);
      setMessageType('error');
    }
  };

  const enviar = async (op) => {
    if (!canSubmit) return;

    const opciones = {
      nombre_archivo: nombreArchivo,
      reasignar_existentes: reasignarExistentes,
      reiniciar_estado: reiniciarEstado,
      permitir_con_linea: permitirConLinea,
    };

    try {
      setLoading(true);
      setResultado(null);

      const bloques = [];
      for (let i = 0; i < rows.length; i += APPLY_CHUNK_SIZE) {
        bloques.push(rows.slice(i, i + APPLY_CHUNK_SIZE));
      }

      const agregado = {
        ok: true,
        mode: op,
        resumen: {
          filas_recibidas: 0,
          filas_validas: 0,
          filas_invalidas: 0,
          duplicadas_en_archivo: 0,
          insertadas: 0,
          reasignadas: 0,
          omitidas: 0,
          prioridad_sobre_linea: 0,
          ejecutivo_distinto_al_de_linea: 0,
        },
        filas_invalidas: [],
        omitidas: [],
        prioridad_sobre_linea: [],
      };

      for (let i = 0; i < bloques.length; i += 1) {
        if (bloques.length > 1) {
          setMessage(`${op === 'apply' ? 'Aplicando' : 'Validando'} bloque ${i + 1} de ${bloques.length}...`);
          setMessageType('success');
        }

        const resp = await cargarLeadsMasivo(user, { op, ...opciones, rows: bloques[i] });
        if (!resp?.ok) {
          throw new Error(resp?.error || `Falló el bloque ${i + 1} de ${bloques.length}`);
        }

        Object.keys(agregado.resumen).forEach((k) => {
          agregado.resumen[k] += Number(resp?.resumen?.[k] || 0);
        });
        agregado.filas_invalidas.push(...(resp.filas_invalidas || []));
        agregado.omitidas.push(...(resp.omitidas || []));
        agregado.prioridad_sobre_linea.push(...(resp.prioridad_sobre_linea || []));
        agregado.opciones = resp.opciones;
        agregado.build_tag = resp.build_tag;
      }

      setResultado(agregado);
      setMessage(
        op === 'apply'
          ? `Carga aplicada: ${agregado.resumen.insertadas} insertadas, ${agregado.resumen.reasignadas} reasignadas, ${agregado.resumen.omitidas} omitidas.`
          : `Validación lista: ${agregado.resumen.insertadas} se insertarían, ${agregado.resumen.reasignadas} se reasignarían, ${agregado.resumen.omitidas} se omitirían.`,
      );
      setMessageType('success');
    } catch (error) {
      setMessage(`Error en la carga: ${error?.message || 'error desconocido'}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const thStyle = { backgroundColor: 'var(--if-primary)', color: 'white' };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Carga Masiva de Leads</h1>
        <p className="text-gray-600">Asignación masiva de leads a ejecutivos desde un Excel</p>
      </div>

      <div className="seccion-datos">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plantilla</label>
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              disabled={cargandoCatalogos || !!catalogosError}
              onClick={handleDownloadTemplate}
            >
              <MdDownload className="w-4 h-4" />
              Descargar plantilla Excel
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Trae las hojas auxiliares <strong>{HOJA_EJECUTIVOS}</strong> ({ejecutivos.length} activos) y{' '}
              <strong>{HOJA_ORIGENES}</strong> ({origenes.length}) con los valores permitidos.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={cargandoCatalogos || !!catalogosError}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-500 mt-2">
              Hoja <strong>{HOJA_LEADS}</strong> con columnas RUT, EJECUTIVO y ORIGEN. FECHA_ASIG es opcional
              (por defecto, la fecha de hoy).
            </p>
          </div>
        </div>

        {cargandoCatalogos && (
          <div className="mt-4 p-3 rounded bg-blue-50 border border-blue-200 text-blue-900 text-sm">
            Cargando ejecutivos y orígenes...
          </div>
        )}

        {catalogosError && (
          <div className="mt-4 p-3 rounded bg-red-100 border border-red-300 text-red-700 text-sm">
            {catalogosError}
          </div>
        )}

        {message && (
          <div
            className={`mt-4 p-3 rounded ${
              messageType === 'success'
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-red-100 text-red-700 border border-red-300'
            }`}
          >
            {message}
          </div>
        )}

        <div className="mt-4 p-3 rounded border border-gray-200 bg-gray-50 space-y-2">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={reasignarExistentes}
              onChange={(e) => setReasignarExistentes(e.target.checked)}
            />
            <span>
              <strong>Reasignar leads que ya existen.</strong> Sin esto, un RUT que ya está en la tabla de leads se
              omite y queda registrado en el resumen.
            </span>
          </label>

          {reasignarExistentes && (
            <label className="flex items-start gap-2 text-sm text-gray-700 ml-6">
              <input
                type="checkbox"
                className="mt-1"
                checked={reiniciarEstado}
                onChange={(e) => setReiniciarEstado(e.target.checked)}
              />
              <span>
                Reiniciar el estado a <em>Sin gestión</em> al reasignar. Si lo desmarcas, un lead que estaba en
                Perdidos seguirá ahí y el ejecutivo nuevo no lo verá en <em>En gestión</em>.
              </span>
            </label>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={permitirConLinea}
              onChange={(e) => setPermitirConLinea(e.target.checked)}
            />
            <span>
              <strong>Cargar también RUT que ya son clientes con línea.</strong> El ejecutivo del Excel manda sobre
              el de la línea, y el de la línea queda guardado para poder compararlos. Si lo desmarcas, esos RUT se
              omiten y quedan listados en el resumen.
            </span>
          </label>
        </div>

        {rows.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn-primary" disabled={!canSubmit} onClick={() => enviar('preview')}>
              {loading ? 'Procesando...' : 'Validar sin guardar'}
            </button>
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              disabled={!canSubmit}
              onClick={() => enviar('apply')}
            >
              <MdCloudUpload className="w-4 h-4" />
              {loading ? 'Aplicando...' : `Aplicar carga (${rows.length})`}
            </button>
          </div>
        )}

        {resultado && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Resultado {resultado.mode === 'apply' ? '(aplicado)' : '(simulación, no se guardó nada)'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                ['Insertadas', resultado.resumen.insertadas],
                ['Reasignadas', resultado.resumen.reasignadas],
                ['Omitidas', resultado.resumen.omitidas],
                ['Rechazadas por la Lambda', resultado.resumen.filas_invalidas],
                ['Sobre cliente con línea', resultado.resumen.prioridad_sobre_linea],
              ].map(([label, valor]) => (
                <div key={label} className="p-3 rounded border border-gray-200 bg-white">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="text-xl font-semibold text-gray-800">{valor}</div>
                </div>
              ))}
            </div>

            {resultado.omitidas.length > 0 && (
              <div className="mt-4 tabla-container" style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto' }}>
                <table className="w-full" style={{ fontSize: '0.8125rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={thStyle}>Fila</th>
                      <th style={thStyle}>RUT</th>
                      <th style={thStyle}>Motivo de la omisión</th>
                      <th style={thStyle}>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.omitidas.slice(0, 300).map((o, i) => (
                      <tr key={`${o.rut}-${i}`}>
                        <td style={{ textAlign: 'center' }}>{o.fila ?? '-'}</td>
                        <td>{o.rut}</td>
                        <td>{describirMotivo(o.motivo)}</td>
                        <td>{o.ejecutivo_linea || o.ejec_asig_actual || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {resultado.prioridad_sobre_linea.length > 0 && (
              <>
                <div className="mt-4 p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
                  {resultado.resumen.ejecutivo_distinto_al_de_linea} de {resultado.resumen.prioridad_sobre_linea} lead(s)
                  quedan con un ejecutivo distinto al que tiene la línea en el sistema factoring. La asignación de esta
                  carga es la que manda; el ejecutivo de la línea se guarda para poder compararlos y se muestra en{' '}
                  <em>En gestión</em>.
                </div>
                <div className="mt-2 tabla-container" style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="w-full" style={{ fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={thStyle}>Fila</th>
                        <th style={thStyle}>RUT</th>
                        <th style={thStyle}>Ejecutivo asignado (manda)</th>
                        <th style={thStyle}>Ejecutivo en líneas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.prioridad_sobre_linea
                        .filter((p) => !p.mismo_ejecutivo)
                        .slice(0, 300)
                        .map((p, i) => (
                          <tr key={`${p.rut}-${i}`}>
                            <td style={{ textAlign: 'center' }}>{p.fila ?? '-'}</td>
                            <td>{p.rut}</td>
                            <td>{p.ejec_asig}</td>
                            <td>{p.ejecutivo_linea}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {invalidRows.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Filas con problemas en el archivo ({invalidRows.length})
            </h3>
            <div className="tabla-container" style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto' }}>
              <table className="w-full" style={{ fontSize: '0.8125rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={thStyle}>Fila</th>
                    <th style={thStyle}>RUT</th>
                    <th style={thStyle}>Problema</th>
                    <th style={thStyle}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {invalidRows.slice(0, 300).map((r, i) => (
                    <tr key={`${r.fila}-${i}`}>
                      <td style={{ textAlign: 'center' }}>{r.fila}</td>
                      <td>{r.rut || '-'}</td>
                      <td>{describirMotivo(r.motivo)}</td>
                      <td>{r.detalle || r.ejecutivo || r.origen || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Vista previa de la carga ({rows.length} filas)
            </h3>
            <div className="tabla-container" style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto' }}>
              <table className="w-full" style={{ minWidth: '720px', fontSize: '0.8125rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={thStyle}>Fila</th>
                    <th style={thStyle}>RUT</th>
                    <th style={thStyle}>Ejecutivo</th>
                    <th style={thStyle}>Usuario SF</th>
                    <th style={thStyle}>Equipo</th>
                    <th style={thStyle}>Origen</th>
                    <th style={thStyle}>Fecha asignación</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map((r) => (
                    <tr key={r.rut}>
                      <td style={{ textAlign: 'center' }}>{r.fila}</td>
                      <td>{r.rut}</td>
                      <td>{r.ejecutivo_nombre || '-'}</td>
                      <td>{r.ejecutivo}</td>
                      <td>{r.equipo}</td>
                      <td>{r.origen}</td>
                      <td style={{ textAlign: 'center' }}>{r.fecha_asig || 'hoy'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 200 && (
              <p className="text-xs text-gray-500 mt-2">Se muestran las primeras 200 filas de {rows.length}.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CargaMasivaLeads;
